import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'universal-cookie';
import { handleResponseCode } from '../../../../../app/utilities/helpers';
import GetProxy from '../../../../../app/GetProxy';

const cookies = new Cookies();
const proxy = GetProxy();

const BASE = '/sassp/email-scheduler';

// Every request in this slice is built from this map, so pointing the page at
// the final backend paths is a one-line change per call.
export const ENDPOINTS = {
    // pageNumber is 0-based.
    list: (pageNumber, itemsPerPage) => `${BASE}/list/${pageNumber}/${itemsPerPage}`,
    // Creates when the payload has no id, updates when it does.
    save: () => `${BASE}/save`,
    delete: (id) => `${BASE}/${id}`,
    // `active` is a boolean; toggling it leaves the cadence untouched.
    status: (id, active) => `${BASE}/status/${id}/${active}`,
    types: () => `${BASE}/availableTypes`,
    /* Templates are keyed by email type rather than by schedule, and live
       outside the scheduler path. */
    templateSave: () => `/email-template/save`,
    templateGet: () => `/email-template/get`,
    /* The email types that still have nothing configured. An empty array means
       every type is already scheduled, so there is nothing left to add. */
    templatePending: () => `/email-template/pending`,
};

const authHeaders = () => ({
    Accept: "application/json",
    "Content-Type": "application/json",
    "DTOP-API-TOKEN": cookies.get("apitoken"),
    USERNAME: cookies.get("username"),
});

export const emailConfigurationList = createAsyncThunk(
    "post/emailConfigurationList",
    async (props) => {
        try {
            const { pageNumber, itemsPerPage, payload } = props;
            const response = await fetch(`${proxy}${ENDPOINTS.list(pageNumber, itemsPerPage)}`,
                {
                    method: "POST",
                    headers: authHeaders(),
                    body: JSON.stringify(payload),
                }
            );
            handleResponseCode(response);
            const data = await response.json();

            return data;
        } catch (error) {
            console.error("Error:", error);
            throw error;
        }
    }
);

export const emailConfigurationDelete = createAsyncThunk("DELETE/emailConfigurationDelete",
    async (props) => {
        try {
            const { id } = props;
            const response = await fetch(`${proxy}${ENDPOINTS.delete(id)}`, {
                method: "DELETE",
                headers: authHeaders(),
            }
            );
            handleResponseCode(response);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error:", error);
            throw error;
        }
    });

export const emailConfigurationSaveAndUpdate = createAsyncThunk("post/emailConfigurationSaveAndUpdate",
    async (props) => {
        try {
            const { savePayload } = props;
            const response = await fetch(`${proxy}${ENDPOINTS.save()}`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(savePayload)
            });
            handleResponseCode(response);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error:", error);
            throw error;
        }
    });

// Flips the enabled/disabled toggle in the Status column.
export const emailConfigurationStatusChange = createAsyncThunk("put/emailConfigurationStatusChange",
    async (props) => {
        try {
            const { id, active } = props;
            const response = await fetch(`${proxy}${ENDPOINTS.status(id, active)}`, {
                method: "POST",
                headers: authHeaders(),
            });
            handleResponseCode(response);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error:", error);
            throw error;
        }
    });

// Saves the email template (subject + message) attached to a schedule row.
export const emailTemplateSave = createAsyncThunk("post/emailTemplateSave",
    async (props) => {
        try {
            const { templatePayload } = props;
            const response = await fetch(`${proxy}${ENDPOINTS.templateSave()}`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(templatePayload)
            });
            handleResponseCode(response);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error:", error);
            throw error;
        }
    });

// The template behind the Template icon. Keyed by email type rather than by
// schedule id, so the popup asks for it each time it opens.
export const emailTemplateGet = createAsyncThunk("post/emailTemplateGet",
    async (props) => {
        try {
            const { emailType } = props;
            const response = await fetch(`${proxy}${ENDPOINTS.templateGet()}`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ emailType }),
            });
            handleResponseCode(response);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error:", error);
            throw error;
        }
    });

// Options for the Type dropdown on the Add / Update form. The backend returns a
// plain array of type names, e.g. ["ASSET_APPROVAL_REMINDER", ...].
export const emailConfigurationTypeList = createAsyncThunk("get/emailConfigurationTypeList", async () => {
    try {
        const response = await fetch(`${proxy}${ENDPOINTS.types()}`, {
            method: "GET",
            headers: authHeaders(),
        });
        handleResponseCode(response);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error:", error);
        throw error;
    }
});

/* Which email types are still unconfigured. Drives whether Add is offered at
   all: with none left there is nothing a new schedule could be for. Returns a
   plain array of type names, e.g. ["Licence Expiry Alert"]. */
export const emailTemplatePendingList = createAsyncThunk("get/emailTemplatePendingList", async () => {
    try {
        const response = await fetch(`${proxy}${ENDPOINTS.templatePending()}`, {
            method: "GET",
            headers: authHeaders(),
        });
        handleResponseCode(response);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error:", error);
        throw error;
    }
});

const initialState = {
    tableLoading: false,
    listTableData: [],
    tableCountLoading: false,
    listTableDataCount: [],
    listDelete: '',
    listDeleteLoading: false,
    saveAndUpdating: '',
    saveAndUpdatingLoading: false,
    statusChangeLoading: false,
    tableReload: false,
    scheduleTypeList: [],
    scheduleTypeListLoading: false,
    templateSaveLoading: false,
    templateDetail: null,
    templateLoading: false,
    // Starts null rather than [] so the Add button can tell "not asked yet"
    // from "asked, and there is nothing left".
    pendingTemplateTypes: null,
    pendingTemplateTypesLoading: false,
};
export const EmailScheduleConfigSlice = createSlice({
    name: 'EmailScheduleConfigSlice',
    initialState: initialState,
    reducers: {
        resetEmailScheduleConfigData: (state) => {
            state.saveAndUpdating = '';
            state.listDelete = '';
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(emailConfigurationList.pending, (state) => {
                state.tableLoading = true;
            })
            .addCase(emailConfigurationList.fulfilled, (state, action) => {
                state.listTableData = action.payload;
                state.tableLoading = false;
                state.tableReload = false;
            })
            .addCase(emailConfigurationList.rejected, (state) => {
                state.tableLoading = false;
            })

            .addCase(emailConfigurationDelete.pending, (state) => {
                state.listDeleteLoading = true;
            })
            .addCase(emailConfigurationDelete.fulfilled, (state, action) => {
                state.listDelete = action.payload;
                state.listDeleteLoading = false;
                state.tableReload = true;
            })
            .addCase(emailConfigurationDelete.rejected, (state) => {
                state.listDeleteLoading = false;
            })

            .addCase(emailConfigurationSaveAndUpdate.pending, (state) => {
                state.saveAndUpdatingLoading = true;
            })
            .addCase(emailConfigurationSaveAndUpdate.fulfilled, (state, action) => {
                state.saveAndUpdating = action.payload;
                state.saveAndUpdatingLoading = false;
                state.tableReload = true;
            })
            .addCase(emailConfigurationSaveAndUpdate.rejected, (state) => {
                state.saveAndUpdatingLoading = false;
            })

            .addCase(emailConfigurationStatusChange.pending, (state) => {
                state.statusChangeLoading = true;
            })
            .addCase(emailConfigurationStatusChange.fulfilled, (state) => {
                state.statusChangeLoading = false;
                state.tableReload = true;
            })
            .addCase(emailConfigurationStatusChange.rejected, (state) => {
                state.statusChangeLoading = false;
            })

            .addCase(emailTemplateSave.pending, (state) => {
                state.templateSaveLoading = true;
            })
            .addCase(emailTemplateSave.fulfilled, (state) => {
                state.templateSaveLoading = false;
                state.tableReload = true;
            })
            .addCase(emailTemplateSave.rejected, (state) => {
                state.templateSaveLoading = false;
            })

            .addCase(emailConfigurationTypeList.pending, (state) => {
                state.scheduleTypeListLoading = true;
            })
            .addCase(emailConfigurationTypeList.fulfilled, (state, action) => {
                state.scheduleTypeList = action.payload;
                state.scheduleTypeListLoading = false;
            })
            .addCase(emailConfigurationTypeList.rejected, (state) => {
                state.scheduleTypeListLoading = false;
            })

            .addCase(emailTemplateGet.pending, (state) => {
                state.templateLoading = true;
                // Cleared on the way in so the popup never shows the template
                // belonging to the row opened before it.
                state.templateDetail = null;
            })
            .addCase(emailTemplateGet.fulfilled, (state, action) => {
                state.templateDetail = action.payload;
                state.templateLoading = false;
            })
            .addCase(emailTemplateGet.rejected, (state) => {
                state.templateDetail = null;
                state.templateLoading = false;
            })

            .addCase(emailTemplatePendingList.pending, (state) => {
                state.pendingTemplateTypesLoading = true;
            })
            .addCase(emailTemplatePendingList.fulfilled, (state, action) => {
                state.pendingTemplateTypes = Array.isArray(action.payload)
                    ? action.payload
                    : [];
                state.pendingTemplateTypesLoading = false;
            })
            .addCase(emailTemplatePendingList.rejected, (state) => {
                // Unknown, not empty: a failed check must not be what takes Add
                // away from the user.
                state.pendingTemplateTypes = null;
                state.pendingTemplateTypesLoading = false;
            });

    },

});
export const { resetEmailScheduleConfigData } = EmailScheduleConfigSlice.actions;
export default EmailScheduleConfigSlice.reducer;
